import useLocale from 'helpers/hooks/useLocale';
import aggregatedQuantity from 'helpers/utils/aggregatedQuantity';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { sdk } from 'sdk';
import { Cart, TStoreInventory } from 'shared/types/cart';
import { Wishlist } from 'shared/types/wishlist';

const useStock = () => {
  const [fluentStocks, setFluentStocks] = useState<TStoreInventory[]>([]);
  const [isStockChecked, setIsStockChecked] = useState(false);
  const [count, setCount] = useState(0);
  const { getCountry } = useLocale();

  const getProductStock = useCallback(async (productSKUs: string[], countryCode: string = getCountry()) => {
    const extensions = sdk.composableCommerce;
    const res = await extensions.stock.getProductInventory({ productSKUs, countryCode });
    if (!res.isError) {
      return res.data as TStoreInventory[];
    }

    return [];
  }, []);

  // Fetch the updated inventory of stocks from Fluent OMS.
  const triggerGetStock = (productSKUs: string[]) => {
    useEffect(() => {
      const getStock = async () => {
        const stockData = await getProductStock(productSKUs as string[], getCountry());
        stockData.length > 0 ? setFluentStocks(stockData) : setFluentStocks([{} as any]);
        setIsStockChecked(true);
      };

      // Only fetch stock data if `productSKUs` have changed and `fluentStocks` is empty
      if (
        (productSKUs.length > 0 && fluentStocks.length === 0 && count === 0) ||
        productSKUs.length > fluentStocks[0]?.productsStock?.length
      ) {
        setCount(1);
        setTimeout(() => {
          getStock();
        }, 10);
      } else {
        setIsStockChecked(true);
      }
    }, [productSKUs]);
  };

  const recheckStocks = async (productSKUs: string[]) => {
    const getStock = async () => {
      const stockData = await getProductStock(productSKUs as string[], getCountry());
      setFluentStocks(stockData);
      setIsStockChecked(true);
    };

    // Only fetch stock data if `productSKUs` have changed and `fluentStocks` is empty
    if (productSKUs.length > 0 && fluentStocks.length === 0 && count === 0) {
      setCount(1);
      setTimeout(() => {
        getStock();
      }, 10);
    } else {
      setIsStockChecked(true);
    }
  };

  const itemsWithStockStatus = (data: Wishlist | Cart) => {
    return useMemo(() => {
      return data?.lineItems?.map((item) => {
        // Default stock status is true if stock hasn't been checked yet
        let isOnStock = true;
        let fluentAvailableQuantity = 1;
        if (isStockChecked) {
          fluentAvailableQuantity = aggregatedQuantity(item.variant?.sku, fluentStocks);
          isOnStock = fluentAvailableQuantity > 0; // just check if stock is not zero
        }
        return { ...item, isOnStock, fluentAvailableQuantity };
      });
    }, [data?.lineItems, fluentStocks, isStockChecked]);
  };

  return {
    fluentStocks,
    getProductStock,
    isStockChecked,
    itemsWithStockStatus,
    triggerGetStock,
    recheckStocks,
  };
};

export default useStock;
